// Sentry SDK initialization for the BROWSER (client) side.
// We point it at our self-hosted Bugsink instance via the SENTRY_DSN env
// var. If the var is empty (e.g. in a fresh checkout), Sentry stays silent
// — no errors, no network requests — so dev unblocks regardless.

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/blog\"},{\"path\":\"/calendar\"},{\"path\":\"/content\"},{\"path\":\"/dashboard\"},{\"path\":\"/editor\"},{\"path\":\"/editor/canvas\"},{\"path\":\"/files\"},{\"path\":\"/generate\"},{\"path\":\"/notifications\"},{\"path\":\"/profile\"},{\"path\":\"/publish-preview\"},{\"path\":\"/repost\"},{\"path\":\"/settings\"},{\"path\":\"/team\"},{\"path\":\"/accept-invite\"},{\"path\":\"/admin/affiliates\"},{\"path\":\"/affiliate\"},{\"path\":\"/affiliate/dashboard\"},{\"path\":\"/auth/reset-password\"},{\"path\":\"/contact\"},{\"path\":\"/cookies\"},{\"path\":\"/dev/designs\"},{\"path\":\"/forgot-password\"},{\"path\":\"/login\"},{\"path\":\"/onboard\"},{\"path\":\"/privacy\"},{\"path\":\"/sentry-client-test\"},{\"path\":\"/signup\"},{\"path\":\"/terms\"},{\"path\":\"/trial-reset\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.1.6";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from "@sentry/nextjs";

// Required by Sentry to instrument App Router navigations. Without this
// export the SDK logs "ACTION REQUIRED" on every build.
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,
    // Errors only — no performance, no replay (cheapest, fits self-hosted)
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Environment tag so dev/prod errors are easy to filter in the UI
    environment: process.env.NEXT_PUBLIC_VERCEL_ENV ||
      (process.env.NODE_ENV === "production" ? "production" : "development"),

    // Tag every client event with the BUILD_ID baked at build time so the UI
    // links a client-side error to a specific deploy. NEXT_PUBLIC_BUILD_ID
    // is set by scripts/deploy-prod.sh (or scripts/build-and-deploy.sh) —
    // falls back to "dev" when not present (local checkouts).
    release:
      process.env.NEXT_PUBLIC_BUILD_ID ||
      `dev-${process.env.NODE_ENV || "unknown"}`,

    // Quiet down noisy non-actionable errors
    ignoreErrors: [
      // Browser extensions / third-party scripts
      /^chrome-extension/,
      /^moz-extension/,
      // Network blips that aren't our app's problem
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      // Chrome MCP extension noise observed earlier in the session
      "A listener indicated an asynchronous response by returning true",
    ],

    // Don't blow up the bundle with debug strings on prod
    debug: false,
  });
}

// ── Telegram ops reporting for browser errors (2026-07-21) ─────────────────
// Complements Sentry/Bugsink: unhandled window errors + promise rejections
// are POSTed to /api/ops/client-error, which forwards a rich incident
// report (user + brand attached) to the Telegram ops chat. Self-capped at
// 5 reports per page session; server-side signature throttle dedups the
// rest. Never throws.
if (typeof window !== "undefined") {
  let opsReportsSent = 0;
  const reportToOps = (message: string, stack?: string) => {
    try {
      if (opsReportsSent >= 5) return;
      opsReportsSent += 1;
      void fetch("/api/ops/client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: String(message || "").slice(0, 500),
          stack: stack ? String(stack).slice(0, 800) : undefined,
          url: window.location.pathname + window.location.search,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* reporting must never break the page */
    }
  };
  window.addEventListener("error", (e) => {
    reportToOps(e?.message || "window error", (e?.error as Error | undefined)?.stack);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r: any = e?.reason;
    reportToOps(
      typeof r === "string" ? r : r?.message || "unhandled promise rejection",
      r?.stack
    );
  });
}
